import { useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import WhyBox from '@/components/ui/WhyBox';

/**
 * Replaces `window.confirm` / `window.prompt`, which cannot be themed or
 * translated. Set `promptLabel` to collect a value (the naming step when promoting
 * a production to a reusable style).
 */
export default function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  warning,
  confirmLabel,
  cancelLabel = 'Cancel',
  destructive = false,
  promptLabel,
  promptPlaceholder,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: React.ReactNode;
  /** Consequences the user should read before confirming. */
  warning?: React.ReactNode;
  confirmLabel: string;
  cancelLabel?: string;
  destructive?: boolean;
  promptLabel?: string;
  promptPlaceholder?: string;
  onConfirm: (value: string) => void;
}) {
  const [value, setValue] = useState('');

  // Clear any previously typed value each time the dialog is reopened.
  useEffect(() => {
    if (open) setValue('');
  }, [open]);

  const submit = () => {
    if (promptLabel && !value.trim()) return;
    onConfirm(value.trim());
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[460px]">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>

        {warning && (
          <WhyBox variant={destructive ? 'danger' : 'info'} className="mb-4">
            {warning}
          </WhyBox>
        )}

        {promptLabel && (
          <label className="block">
            <span className="mb-1.5 block text-[13px] font-medium">{promptLabel}</span>
            <input
              autoFocus
              type="text"
              value={value}
              placeholder={promptPlaceholder}
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && submit()}
              className="w-full rounded-md border border-input bg-elevated px-3 py-2 text-[13px] outline-none focus:border-primary"
            />
          </label>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {cancelLabel}
          </Button>
          <Button variant={destructive ? 'destructive' : 'default'} disabled={!!promptLabel && !value.trim()} onClick={submit}>
            {confirmLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
